import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import {
    answerQuestion,
    createKnowledge,
    deleteKnowledge,
    dismissQuestion,
    getAutopilotOverview,
    getComplianceSettings,
    getInterviews,
    getKnowledge,
    getQuestions,
    getReview,
    reviewInvite,
    reviewReject,
    saveAutopilotSettings,
    setInterviewStatus,
    updateKnowledge,
} from "../services/autopilotApi";

/**
 * «Автопилот найма» — [Claude] 2026-11-02.
 * Календарь собеседований (1 час на кандидата, заключение ИИ, комплект документов),
 * вопросы кандидатов к рекрутеру (ответ уходит кандидату и сохраняется в базу знаний),
 * решения по кандидатам, которых ИИ не рекомендовал, база знаний и настройки.
 */

const TABS = [
    ["calendar", "Календарь собеседований"],
    ["questions", "Вопросы к рекрутеру"],
    ["review", "Не рекомендованы ИИ"],
    ["knowledge", "База знаний"],
    ["settings", "Настройки"],
];

const KIT_STATUS = {
    generated: "комплект сформирован — распечатайте",
    printed: "распечатан",
    signed: "подписан",
    verified: "проверен",
    cancelled: "отменён",
};

function fmtDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
    if (!m) return iso || "";
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const days = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
    return days[d.getDay()] + ", " + m[3] + "." + m[2] + "." + m[1];
}

function Section({ children }) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">{children}</div>;
}

function Empty({ text }) {
    return <p className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">{text}</p>;
}

function Conclusion({ verdict }) {
    if (!verdict || !Object.keys(verdict).length) return null;
    return (
        <div className="mt-3 space-y-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-800">
            <p>
                <b>Заключение ИИ</b>
                {verdict.score != null ? <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs font-semibold">{verdict.score} / 100</span> : null}
            </p>
            {verdict.conclusion ? <p className="whitespace-pre-line">{verdict.conclusion}</p> : null}
            {verdict.strengths?.length ? (
                <div><p className="font-semibold text-emerald-800">Почему подходит</p><ul className="ml-5 list-disc">{verdict.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
            ) : null}
            {verdict.risks?.length ? (
                <div><p className="font-semibold text-amber-800">Риски</p><ul className="ml-5 list-disc">{verdict.risks.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
            ) : null}
            {verdict.interview_questions?.length ? (
                <div><p className="font-semibold text-slate-700">Что спросить на собеседовании</p><ul className="ml-5 list-disc">{verdict.interview_questions.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
            ) : null}
        </div>
    );
}

function CalendarTab() {
    const { showAlert } = useAlertContext();
    const [rows, setRows] = useState(null);
    const load = useCallback(async () => {
        try {
            const data = await getInterviews();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            showAlert(e?.message || "Не удалось загрузить календарь", "error");
            setRows([]);
        }
    }, [showAlert]);
    useEffect(() => { load(); }, [load]);
    const byDay = useMemo(() => {
        const groups = {};
        (rows || []).forEach((r) => { (groups[r.date] = groups[r.date] || []).push(r); });
        return Object.entries(groups);
    }, [rows]);
    const mark = async (id, status) => {
        try { await setInterviewStatus(id, status); await load(); } catch (e) { showAlert(e?.message || "Ошибка", "error"); }
    };
    if (rows === null) return <p className="text-sm text-slate-500">Загрузка…</p>;
    if (!rows.length) return <Empty text="На ближайшие две недели собеседований нет. Автопилот запишет кандидатов, которые подошли и выбрали время." />;
    return (
        <div className="space-y-5">
            {byDay.map(([day, items]) => (
                <div key={day} className="space-y-3">
                    <h3 className="text-lg font-bold text-slate-900">{fmtDate(day)} · {items.length} собес.</h3>
                    {items.map((r) => (
                        <Section key={r.id}>
                            <div className="flex flex-wrap items-start justify-between gap-3" data-testid={"interview-" + r.id}>
                                <div>
                                    <p className="text-base font-bold text-slate-900">
                                        {r.time}–{r.time_end} · <Link className="hover:underline" to={"/candidates?candidate_id=" + r.candidate_id}>{r.candidate_name || "Кандидат #" + r.candidate_id}</Link>
                                    </p>
                                    <p className="mt-1 text-sm text-slate-600">{r.vacancy_name} · {r.place}</p>
                                    {r.hh_resume_link ? <a className="text-sm text-[#9b7c13] hover:underline" href={r.hh_resume_link} target="_blank" rel="noreferrer">резюме HH</a> : null}
                                </div>
                                <div className="text-right text-sm">
                                    <p className={"rounded-full px-3 py-1 font-semibold " + (r.kit && r.kit.status === "generated" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700")}>
                                        🖨 {r.kit ? (KIT_STATUS[r.kit.status] || r.kit.status) : "комплект не создан"}
                                    </p>
                                    <p className="mt-1 text-slate-500">{r.status === "booked" ? "назначено" : r.status}</p>
                                </div>
                            </div>
                            <Conclusion verdict={r.conclusion} />
                            {r.status === "booked" ? (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button type="button" onClick={() => mark(r.id, "done")} className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white">Собеседование прошло</button>
                                    <button type="button" onClick={() => mark(r.id, "no_show")} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold">Не пришёл</button>
                                    <button type="button" onClick={() => mark(r.id, "cancelled")} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold">Отменить</button>
                                </div>
                            ) : null}
                        </Section>
                    ))}
                </div>
            ))}
        </div>
    );
}

function QuestionCard({ q, onDone }) {
    const { showAlert } = useAlertContext();
    const [answer, setAnswer] = useState("");
    const [save, setSave] = useState(true);
    const [busy, setBusy] = useState(false);
    const send = async () => {
        if (!answer.trim()) return;
        setBusy(true);
        try {
            const res = await answerQuestion(q.id, { answer: answer.trim(), save_to_knowledge: save });
            showAlert(res?.sent ? "Ответ отправлен кандидату" + (save ? " и сохранён в базу знаний" : "") : "Ответ сохранён, но в HH не ушёл — проверьте чат кандидата", res?.sent ? "success" : "warning");
            onDone(q.id);
        } catch (e) {
            showAlert(e?.message || "Не удалось отправить ответ", "error");
        } finally {
            setBusy(false);
        }
    };
    const dismiss = async () => {
        try { await dismissQuestion(q.id); onDone(q.id); } catch (e) { showAlert(e?.message || "Ошибка", "error"); }
    };
    return (
        <Section>
            <div data-testid={"question-" + q.id}>
                <p className="text-sm text-slate-500">
                    <Link className="font-semibold text-slate-900 hover:underline" to={"/candidates?candidate_id=" + q.candidate_id}>{q.candidate_name || "Кандидат"}</Link> · {q.vacancy_name}
                </p>
                <p className="mt-2 text-base font-semibold text-slate-900">{q.question}</p>
                {q.context && q.context !== q.question ? <p className="mt-2 whitespace-pre-line rounded-xl bg-slate-50 p-3 text-sm text-slate-700">Сообщение кандидата: {q.context}</p> : null}
                <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3} placeholder="Ответ кандидату — уйдёт в чат HH" className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm" />
                <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} />
                    Сохранить в базу знаний — бот будет отвечать так сам
                </label>
                <div className="mt-3 flex gap-2">
                    <button type="button" disabled={busy || !answer.trim()} onClick={send} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Отправить ответ</button>
                    <button type="button" onClick={dismiss} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold">Ответил сам в HH</button>
                </div>
            </div>
        </Section>
    );
}

function QuestionsTab() {
    const { showAlert } = useAlertContext();
    const [rows, setRows] = useState(null);
    useEffect(() => {
        getQuestions().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => { showAlert(e?.message || "Ошибка", "error"); setRows([]); });
    }, [showAlert]);
    if (rows === null) return <p className="text-sm text-slate-500">Загрузка…</p>;
    if (!rows.length) return <Empty text="Открытых вопросов нет — бот ответил на всё сам." />;
    return <div className="space-y-3">{rows.map((q) => <QuestionCard key={q.id} q={q} onDone={(id) => setRows((p) => p.filter((x) => x.id !== id))} />)}</div>;
}

function ReviewTab() {
    const { showAlert } = useAlertContext();
    const [rows, setRows] = useState(null);
    useEffect(() => {
        getReview().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => { showAlert(e?.message || "Ошибка", "error"); setRows([]); });
    }, [showAlert]);
    const act = async (fn, id, text) => {
        try { await fn(id); showAlert(text, "success"); setRows((p) => p.filter((x) => x.id !== id)); } catch (e) { showAlert(e?.message || "Ошибка", "error"); }
    };
    if (rows === null) return <p className="text-sm text-slate-500">Загрузка…</p>;
    if (!rows.length) return <Empty text="Нет кандидатов, ожидающих решения." />;
    return (
        <div className="space-y-3">
            {rows.map((r) => (
                <Section key={r.id}>
                    <p className="text-base font-bold text-slate-900">
                        <Link className="hover:underline" to={"/candidates?candidate_id=" + r.candidate_id}>{r.candidate_name || "Кандидат"}</Link>
                        <span className="ml-2 text-sm font-normal text-slate-500">{r.vacancy_name}</span>
                    </p>
                    <Conclusion verdict={r.verdict} />
                    <div className="mt-3 flex gap-2">
                        <button type="button" onClick={() => act(reviewInvite, r.id, "Кандидат получит приглашение и выбор времени")} className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white">Всё равно пригласить</button>
                        <button type="button" onClick={() => act(reviewReject, r.id, "Кандидат отклонён")} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold">Отклонить</button>
                    </div>
                </Section>
            ))}
        </div>
    );
}

function KnowledgeTab() {
    const { showAlert } = useAlertContext();
    const [rows, setRows] = useState(null);
    const [draft, setDraft] = useState({ question: "", answer: "" });
    const load = useCallback(() => {
        getKnowledge().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => { showAlert(e?.message || "Ошибка", "error"); setRows([]); });
    }, [showAlert]);
    useEffect(() => { load(); }, [load]);
    const add = async () => {
        if (!draft.question.trim() || !draft.answer.trim()) return;
        try { await createKnowledge({ ...draft, active: true }); setDraft({ question: "", answer: "" }); load(); } catch (e) { showAlert(e?.message || "Ошибка", "error"); }
    };
    const toggle = async (r) => {
        try {
            if (r.active) await deleteKnowledge(r.id);
            else await updateKnowledge(r.id, { question: r.question, answer: r.answer, vacancy_id: r.vacancy_id, active: true });
            load();
        } catch (e) { showAlert(e?.message || "Ошибка", "error"); }
    };
    return (
        <div className="space-y-3">
            <Section>
                <p className="font-semibold text-slate-900">Добавить ответ вручную</p>
                <input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} placeholder="Вопрос кандидата (например: есть ли ДМС?)" className="mt-2 w-full rounded-xl border border-slate-200 p-2 text-sm" />
                <textarea value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} rows={2} placeholder="Ответ, который бот будет давать" className="mt-2 w-full rounded-xl border border-slate-200 p-2 text-sm" />
                <button type="button" onClick={add} className="mt-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Добавить</button>
            </Section>
            {rows === null ? <p className="text-sm text-slate-500">Загрузка…</p> : null}
            {(rows || []).map((r) => (
                <Section key={r.id}>
                    <div className={r.active ? "" : "opacity-50"}>
                        <p className="font-semibold text-slate-900">{r.question}</p>
                        <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{r.answer}</p>
                        <p className="mt-2 text-xs text-slate-500">{r.source === "recruiter" ? "ответ рекрутера" : "добавлено вручную"}{r.created_by ? " · " + r.created_by : ""}</p>
                        <button type="button" onClick={() => toggle(r)} className="mt-2 text-sm font-semibold text-[#9b7c13] hover:underline">{r.active ? "Отключить" : "Включить"}</button>
                    </div>
                </Section>
            ))}
        </div>
    );
}

function SettingsTab() {
    const { showAlert } = useAlertContext();
    const [value, setValue] = useState(null);
    useEffect(() => {
        getComplianceSettings()
            .then((rows) => {
                const row = (rows || []).find((r) => r.key === "autopilot");
                setValue(row ? row.value : {});
            })
            .catch(() => setValue(false));
    }, []);
    if (value === null) return <p className="text-sm text-slate-500">Загрузка…</p>;
    if (value === false) return <p className="text-sm text-slate-600">Настройки меняет владелец или администратор платформы.</p>;
    const field = (key, label, rows) => (
        <label className="block text-sm">
            <span className="font-semibold text-slate-800">{label}</span>
            {rows ? (
                <textarea rows={rows} value={value[key] || ""} onChange={(e) => setValue({ ...value, [key]: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 p-2" />
            ) : (
                <input value={value[key] ?? ""} onChange={(e) => setValue({ ...value, [key]: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 p-2" />
            )}
        </label>
    );
    const save = async () => {
        const next = { ...value, slot_minutes: Number(value.slot_minutes) || 60, verdict_threshold: Number(value.verdict_threshold) || 70 };
        try { await saveAutopilotSettings(next, "Изменено на странице «Автопилот найма»"); showAlert("Сохранено", "success"); } catch (e) { showAlert(e?.message || "Нет прав на изменение настроек", "error"); }
    };
    return (
        <Section>
            <div className="space-y-3">
                {field("company_summary", "Описание компании (уходит кандидатам перед собеседованием)", 6)}
                {field("interview_place", "Где проходит собеседование", 2)}
                <div className="grid gap-3 md:grid-cols-4">
                    {field("work_start", "Начало приёма")}
                    {field("work_end", "Конец приёма")}
                    {field("slot_minutes", "Длительность, мин")}
                    {field("verdict_threshold", "Порог ИИ, баллов")}
                </div>
                {field("recruiter_name", "Рекрутер, который проводит собеседования")}
                <button type="button" onClick={save} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Сохранить</button>
                <p className="text-xs text-slate-500">Каждое изменение сохраняется как новая версия — откатить можно в админ-панели.</p>
            </div>
        </Section>
    );
}

export default function RecruitingAutopilotPage() {
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") || "calendar";
    const [overview, setOverview] = useState(null);
    useEffect(() => { getAutopilotOverview().then(setOverview).catch(() => setOverview(null)); }, [tab]);
    const counters = {
        calendar: overview?.interviews_today,
        questions: overview?.open_questions,
        review: overview?.stages?.not_recommended?.count,
    };
    return (
        <MainLayout>
            <div className="mx-auto max-w-5xl space-y-4 p-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Автопилот найма</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Платформа сама ведёт кандидатов: уточняет условия вместо отказа, напоминает о тесте, оценивает по совокупности
                        данных, рассказывает о компании и записывает подходящих на собеседование — по часу на кандидата.
                        Вопросы, на которые бот не знает ответа, ждут вас во вкладке «Вопросы к рекрутеру».
                    </p>
                    {overview ? (
                        <p className="mt-2 text-xs text-slate-500">
                            Режим: {overview.flag === "all" ? "все вакансии" : overview.flag === "pilot" ? "пилот на вакансиях " + (overview.pilot_vacancy_ids || []).join(", ") : "выключен"}
                        </p>
                    ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                    {TABS.map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setParams({ tab: key })}
                            className={"rounded-xl px-3 py-2 text-sm font-semibold " + (tab === key ? "bg-slate-900 text-white" : "border border-slate-300 bg-white text-slate-800")}
                        >
                            {label}
                            {counters[key] ? <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">{counters[key]}</span> : null}
                        </button>
                    ))}
                </div>
                {tab === "calendar" ? <CalendarTab /> : null}
                {tab === "questions" ? <QuestionsTab /> : null}
                {tab === "review" ? <ReviewTab /> : null}
                {tab === "knowledge" ? <KnowledgeTab /> : null}
                {tab === "settings" ? <SettingsTab /> : null}
            </div>
        </MainLayout>
    );
}
